import React from 'react'
import useSWRImmutable from 'swr/immutable'

import Loading from '../../components/layout/Loading'
import { getCompany } from '../../../api/companies'
import Circle from '../../components/image/Circle'
import Tag from '../../components/content/Tag'
import { useI18N } from '../../../libraries/i18n/hook'
import Translate from '../../../libraries/i18n/Translate'
import ArrowTopRightSquare from '../../../assets/icons/arrow-top-right-on-square.svg'
import { OuterTabGroup } from '../../components/form/tab-groups'
import { CompanyFull } from '../../../api/companies/types'
import FlagSpain from '../../../assets/spain-flag.svg'
import LocationIcon from '../../../assets/icons/location.svg?react'
import LnIcon from '../../../assets/icons/social/ln.svg?react'
import FbIcon from '../../../assets/icons/social/fb.svg?react'
import IgIcon from '../../../assets/icons/social/ig.svg?react'
import XIcon from '../../../assets/icons/social/x.svg?react'
import YbIcon from '../../../assets/icons/social/yb.svg?react'
import Faqs from '../../components/faqs'
import CompanyDivisions from './Divisions'
import CompanyCoverImage from '../../components/companies/CoverImage'
import GoBackLink from '../../components/layout/GoBackLink'
import CompanyProfileLogo from '../../components/companies/ProfileLogo'
import useScreenSize from '../../../hocs/useScreenSize'
import CompanyJobOffers from './JobOffers'
import DescriptionText from '../../components/content/DescriptionText'

type Props = { id: number }
const TABS: {[x: string]: string } = {
  CULTURE: 'CULTURE',
  WORK: 'WORK',
  CONTACT: 'CONTACT',
  // NOTIF_AND_EVENTS: 'NOTIF_AND_EVENTS',
}
const CompanyProfile: React.FC<Props> = ({ id }) => {
  const { t } = useI18N()
  const { data, isLoading } = useSWRImmutable(['company', id], () => getCompany(id))
  const [activeTab, setActiveTab] = React.useState<string>(TABS.CULTURE)
  const screenSize = useScreenSize()

  if(!data || isLoading) {
    return <Loading size='fill-container' />
  }
  const secondaryTags = data.tags.filter(tag => !tag.isPrimary)

  const scrollToId = (id: string) => {
    if(screenSize.isSm) {
      document.getElementById(id)?.scrollIntoView()
    }
  }
  const showCulture = data.descriptionCulture && data.descriptionCulture.length > 0
  const showWork = data.work?.cities?.length > 0
  const showContact = data.socialUrls?.length > 0 || data.faqs?.length > 0
  const showTabs = showCulture || showWork || showContact
  const tabs = []
  if(showTabs) {
    if(showCulture) {
      tabs.push({ id: TABS.CULTURE, title: t('companies.culture.title', undefined, data.languageCode) })
    }
    if(showWork) {
      tabs.push({ id: TABS.WORK, title: <Translate language={data.languageCode} i18nKey='companies.work.title' values={{ companyName: data.name }} /> })
    }
    // tabs.push({ id: TABS.NOTIF_AND_EVENTS, title: t('common.keepUpToDate') })
    if(showContact) {
      tabs.push({ id: TABS.CONTACT, title: t('companies.contact.title', undefined, data.languageCode) })
    }
  }

  const renderTab = (tab: React.ReactNode, tabId: string) => {
    return (
      <div id={tabId}>
        <div className='hidden sm:block'>
          {tab}
        </div>
        {tabId === activeTab && (
          <div className='block sm:hidden'>
            {tab}
          </div>
        )}
      </div>
    )
  }

  const renderCompanyTags = () => (
    <div className='flex flex-wrap items-center gap-x-2 sm:gap-x-3 font-sans tracking-normal'>
      <Circle src={data.country.flagSrc} />
      <Tag outlined>{data.industry.name}</Tag>
      {data.industry.sector && <Tag outlined>{data.industry.sector.name}</Tag>}
    </div>
  )

  return (
    <div>
      <div>
        <div className='hidden lg:flex justify-end mb-4'><GoBackLink /></div>
        {/* HEADER / tags */}
        <div className='sm:flex items-start'>
          <div className='sm:w-3/4'>
            <div className='flex gap-x-2 sm:gap-x-6 items-center'>
              <div className='inline-block lg:hidden'><GoBackLink /></div>
              <CompanyProfileLogo src={data.logoSrc} alt={data.name} />
              <div className='inline-flex flex-col'>
                <h1 className='inline-block'>{data.name}</h1>
                <div className='hidden sm:inline-block'>{renderCompanyTags()}</div>
              </div>
            </div>
            <div className='block mt-4 sm:hidden'>{renderCompanyTags()}</div>
            <div className='pr-0 sm:pr-10 mt-6'>
              <DescriptionText size='m'>{data.descriptionOperations}</DescriptionText>
            </div>
          </div>
          {(data.resources?.length > 0 || secondaryTags?.length > 0) && (
            <div className='sm:w-96 mt-8 ml-auto sm:mt-0 bg-soft-green p-4 rounded-xl shadow-md text-black text-sm'>
              <div className='mb-3'><strong className='text-xs sm:text-sm'>{t('common.moreInfo', undefined, data.languageCode)}...</strong></div>
              {secondaryTags && (
                <div className={`${data.resources?.length > 0 ? 'border-b' : ''} border-solid border-gray-100 pb-2 mb-2 flex flex-col`}>
                  {secondaryTags.map(tag => <Tag iconSrc={tag.iconSrc} key={tag.label} transparent>{tag.label}</Tag>)}
                </div>
              )}
              {data.resources && (
                <div className='flex flex-col gap-x-2'>
                  {data.resources.map(resource => (
                      <a href={resource.src} key={resource.label} target='_blank' rel='noopener noreferrer'>
                      <Tag transparent iconSrc={ArrowTopRightSquare}>{resource.label}</Tag>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* TABS */}
        <div className='mt-16 sm:mt-24 text-deep-green'>
          {tabs.length > 1 && (
            <OuterTabGroup
              handleClick={(tabId: string) => {
                scrollToId(TABS[tabId])
                setActiveTab(tabId)
              }}
              active={activeTab}
              tabs={tabs}
            />
          )}
          {showCulture && renderTab(
            <CompanyCulture data={data} />
          , TABS.CULTURE)}

          {showWork && renderTab(
            <div className='py-3'>
              <CompanyWork data={data} />
              <CompanyDivisions companyId={id} />
              <div className='mt-6'>
                <CompanyJobOffers companyId={id} />
              </div>
            </div>
          , TABS.WORK)}
          {/* {renderTab(
            <div className='sm:mt-8'>
              <h2>{t('common.keepUpToDate')}</h2>
              <CompanyNotifications companyId={id} />
              <div className='mt-6'>
                <CompanyEvents companyId={id} />
              </div>
            </div>
          , TABS.NOTIF_AND_EVENTS)} */}
          {showContact && renderTab(
            <div className='py-6'>
              <CompanyContact data={data} />
            </div>
          , TABS.CONTACT)}
        </div>
      </div>
    </div>
  )
}

const CompanyCulture: React.FC<{ data: CompanyFull }> = ({ data }) => {
  const { t } = useI18N()
  return (
    <div className='mt-8 flex flex-col sm:flex-row gap-x-12 sm:items-start py-8'>
      <div className='sm:w-1/4'>
        <div className='w-full'>
          <CompanyCoverImage src={data.coverSrc} alt={data.name} />
        </div>
      </div>
      <div className='sm:w-3/4 mt-12 sm:mt-0 sm:min-h-[290px]'>
        <h2 className='hidden sm:block'>{t('companies.culture.title', undefined, data.languageCode)}</h2>
        <div className='mt-4'>
          <DescriptionText size='m'>{data.descriptionCulture}</DescriptionText>
        </div>
      </div>
    </div>
  )
}

const CompanyWork: React.FC<{ data: CompanyFull }> = ({ data }) => {
  const { t } = useI18N()
  const convertCitiesToString = (cities: { name: string }[]) => {
    if (cities.length <= 2) {
      return cities.map(city => city.name).join(` ${t('common.and', undefined, data.languageCode)} `)
    }
    const allButLast = cities.slice(0, -1).map(city => city.name).join(', ')
    const last = cities[cities.length - 1].name
    return `${allButLast} ${t('common.and', undefined, data.languageCode)} ${last}`
  }
  return (
    <div className='sm:mt-8'>
      <h2 className='hidden sm:block'>
        <Translate
          language={data.languageCode}
          i18nKey='companies.work.title'
          values={{ companyName: data.name, country: 'España', defaultValue: 'Trabaja en {{companyName}} en {{country}}'}}
        />
      </h2>
      <div className='sm:inline-flex flex-wrap justify-start items-center sm:mt-4 mb-4 gap-x-2 text-2xs sm:text-xs'>
        <div className='flex sm:inline-flex items-center sm:mb-0 mb-4'>
          <Circle src={FlagSpain} />
          {data.work?.cities?.length > 0 && (
            <div className='ml-4 flex items-center gap-x-2 text-black'>
              <LocationIcon className='stroke-deep-green' />
              <Translate language={data.languageCode} i18nKey='companies.work.officesIn' count={data.work.cities.length} values={{ cities: convertCitiesToString(data.work.cities) }} />
            </div>
          )}
        </div>
        {data.work?.keyInfoLabels?.length > 0 &&  (<div className='inline-flex items-center gap-x-2 text-black'><span className='hidden sm:block'>| </span>{data.work?.keyInfoLabels.join(' | ')}</div>)}
      </div>
      <DescriptionText size='m'>{data.work?.description}</DescriptionText>
    </div>
  )
}

const CompanyContact: React.FC<{ data: CompanyFull }> = ({ data }) => {
  const { t } = useI18N()
  return (
    <div>
      <h2 className='hidden sm:block'>{t('companies.contact.title', undefined, data.languageCode)}</h2>
      <div className='flex flex-col sm:flex-row sm:mt-6'>
        <div className='sm:w-1/3'>
          {data.socialUrls?.length > 0 && (
            <>
              <p className='pt-3 text-md sm:text-base font-light text-black'>
                {/* Sigue a {data.name} en sus redes: */}
                <Translate
                  language={data.languageCode}
                  i18nKey='companies.contact.followUs'
                  values={{ companyName: data.name, defaultValue: 'Sigue a {{companyName}} en sus redes:'}}
                />
              </p>
              <div className='flex items-center gap-x-3 mt-6'>
                {data.socialUrls.map((socialUrl) => {
                  switch(socialUrl.type) {
                    case 'ln': return <a key={`ln${socialUrl.url}`} href={socialUrl.url} target='_blank' rel='noreferrer'><LnIcon className='h-6' /></a>
                    case 'fb': return <a key={`fb${socialUrl.url}`} href={socialUrl.url} target='_blank' rel='noreferrer'><FbIcon className='h-6' /></a>
                    case 'ig': return <a key={`ig${socialUrl.url}`} href={socialUrl.url} target='_blank' rel='noreferrer'><IgIcon className='h-6' /></a>
                    case 'x': return <a key={`x${socialUrl.url}`} href={socialUrl.url} target='_blank' rel='noreferrer'><XIcon className='h-6' /></a>
                    case 'yt': return <a key={`yt${socialUrl.url}`} href={socialUrl.url} target='_blank' rel='noreferrer'><YbIcon className='h-6' /></a>
                  }
                })}
              </div>
            </>
          )}
        </div>
        {data.faqs?.length > 0 && (
          <div className='mt-4 sm:mt-0 sm:w-2/3'>
            <Faqs data={data.faqs} />
          </div>
        )}
      </div>
    </div>
  )
}

export default CompanyProfile
